import { startOfDay } from 'date-fns'

interface IOperationScheduleStoreState {
	schedules: EOperationScheduleTypesModel | null
	isLoading: boolean
	refreshTimeout: ReturnType<typeof setTimeout> | null
}

interface IOperationScheduleStore {
	state: IOperationScheduleStoreState
	fetchSchedules: () => Promise<void>
	getScheduleByType: (type: EOperationScheduleType) => IOperationScheduleModel | null
	getEarliestStartDate: (scheduleType?: EOperationScheduleType) => Date
	getValidUntil: (scheduleType?: EOperationScheduleType) => Date | null
	getHoursUntilExpiry: (scheduleType?: EOperationScheduleType) => number | null
	isChangeAllowed: (dayDate: Date | undefined | null, scheduleType?: EOperationScheduleType) => boolean
	cleanup: () => void
}

export const useOperationScheduleStore = defineStore('operationScheduleStore', (): IOperationScheduleStore => {
	const state = reactive<IOperationScheduleStoreState>({
		schedules: null,
		isLoading: false,
		refreshTimeout: null,
	})

	const fetchSchedules = async () => {
		try {
			state.isLoading = true
			const data = await ApiService.getOperationScheduleEarliestAvailableDate()

			state.schedules = data

			const validUntils = Object.values(data)
				.map(schedule => schedule?.validUntil)
				.filter(date => date !== null && date !== undefined)

			if (validUntils.length > 0) {
				const earliestValidUntil = new Date(Math.min(...validUntils.map(date => date.getTime())))
				// Clear existing timeout
				if (state.refreshTimeout) {
					clearTimeout(state.refreshTimeout)
				}

				// Schedule refresh when earliest validUntil expires
				const timeUntilExpiry = earliestValidUntil.getTime() - Date.now()
				if (timeUntilExpiry > 0) {
					state.refreshTimeout = setTimeout(() => {
						void fetchSchedules()
					}, timeUntilExpiry + 1000) // +1 second buffer to not get into loop
				}
			}
		} catch (error) {
			console.error('Failed to fetch operation schedules:', error)
		} finally {
			state.isLoading = false
		}
	}

	const getScheduleByType = (type: EOperationScheduleType): IOperationScheduleModel | null =>
		state.schedules?.[type] ?? null

	const getEarliestStartDate = (
		scheduleType: EOperationScheduleType = EOperationScheduleType.ORDER_DEADLINE,
	): Date => {
		const operationSchedule = getScheduleByType(scheduleType)

		if (operationSchedule?.earliestAvailableDate) {
			return startOfDay(operationSchedule.earliestAvailableDate)
		}

		// Default fallback - in two days if no API data
		return startOfDay(new Date(Date.now() + FALLBACK_DAYS_IN_HOURS * HOUR_IN_MS))
	}

	const getValidUntil = (
		scheduleType: EOperationScheduleType = EOperationScheduleType.ORDER_DEADLINE,
	): Date | null => {
		const operationSchedule = getScheduleByType(scheduleType)
		return operationSchedule?.validUntil ?? null
	}

	const getHoursUntilExpiry = (
		scheduleType: EOperationScheduleType = EOperationScheduleType.ORDER_DEADLINE,
	): number | null => {
		const validUntil = getValidUntil(scheduleType)
		if (!validUntil) return null

		const now = new Date()
		const timeDifference = validUntil.getTime() - now.getTime()

		if (timeDifference <= 0) return null

		return Math.floor(timeDifference / HOUR_IN_MS)
	}

	const isChangeAllowed = (
		dayDate: Date | undefined | null,
		scheduleType: EOperationScheduleType = EOperationScheduleType.ORDER_DEADLINE,
	): boolean => {
		if (!dayDate) return false
		const earliestStartDate = getEarliestStartDate(scheduleType)
		return startOfDay(dayDate).getTime() >= earliestStartDate.getTime()
	}

	const cleanup = () => {
		if (state.refreshTimeout) {
			clearTimeout(state.refreshTimeout)
			state.refreshTimeout = null
		}
	}

	if (!state.schedules && !state.isLoading) {
		void fetchSchedules()
	}

	return {
		state: readonly(state) as IOperationScheduleStoreState,
		fetchSchedules,
		getScheduleByType,
		getEarliestStartDate,
		getValidUntil,
		getHoursUntilExpiry,
		isChangeAllowed,
		cleanup,
	}
})
